import React,{ useEffect, useMemo, useRef, useState } from "react";
import "./user.css";
import { useDispatch, useSelector } from 'react-redux';
import { fetchThemeData } from '../../admin/themeSlice';
import { selectAdminToken } from '../../admin/sessionSlice';
import { useLocation, useSearchParams } from "react-router-dom";
import { applyThemeBodyBackground } from "../../utils/themeBodyBackground";
import { ensureGoogleFont } from "../../admin/components/TextStyleControls";

const FROM_MOBILE_APP_STORAGE_KEY = 'lcp_fromMobileApp';

/** Routes where the header Back button is hidden (jury flow + mobile WebView entry). */
function shouldHideBackButton(pathname, isFromMobileApp) {
  const p = pathname.replace(/\/+$/, "") || "/";
  if (p.startsWith("/jury")) return true;
  if (isFromMobileApp && isHideBackTargetPath(p)) return true;
  return false;
}

/** Routes where the header Back button is hidden when opened from the mobile app WebView. */
function isHideBackTargetPath(pathname) {
  const p = pathname.replace(/\/+$/, '') || '/';
  if (p === '/' || p === '/login') return true;
  return ['/yoga-poses', '/yoga-upload', '/yoga-gallery'].includes(p);
}

function USER({children }) {
  const dispatch = useDispatch();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const {companyLogoUrl,backButtonUrl} = useSelector(state => state.ui);
    const [logoUrl,setLogoUrl]=useState("");
    const [backUrl,setBackUrl]=useState("");

    const { status, user, error } = useSelector(state => state.auth);
    const { status: themeStatus, data: themeData } = useSelector(state => state.theme);
    const adminToken = useSelector(selectAdminToken);
 

  const applyCustomTheme = (theme) => {
    if (!theme) return;
    const t = theme.colors && typeof theme.colors === "object" ? theme.colors : theme;
    if (t.text_color != null) document.documentElement.style.setProperty("--text-color", t.text_color);
    if (t.ui_color_1 != null) document.documentElement.style.setProperty("--ui-color-1", t.ui_color_1);
    if (t.ui_color_2 != null) document.documentElement.style.setProperty("--ui-color-2", t.ui_color_2);
    if (t.option_color != null) document.documentElement.style.setProperty("--option-color", t.option_color);
    if (t.option_text_color != null) document.documentElement.style.setProperty("--option-text-color", t.option_text_color);

    const fontFamily = theme.font_family || t.font_family;
    if (fontFamily) {
      document.documentElement.style.setProperty("--wgt-font-family", String(fontFamily));
      ensureGoogleFont(String(fontFamily));
    } else {
      document.documentElement.style.removeProperty("--wgt-font-family");
    }

    const desk = theme.font_size_desktop ?? t.font_size_desktop;
    const mob = theme.font_size_mobile ?? t.font_size_mobile;
    if (desk != null && String(desk).trim() !== "") {
      document.documentElement.style.setProperty("--wgt-font-size-desktop", `${desk}px`);
    }
    if (mob != null && String(mob).trim() !== "") {
      document.documentElement.style.setProperty("--wgt-font-size-mobile", `${mob}px`);
    }

    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    const sizePx = isMobile
      ? (mob != null && String(mob).trim() !== "" ? mob : desk)
      : (desk != null && String(desk).trim() !== "" ? desk : mob);
    if (sizePx != null && String(sizePx).trim() !== "") {
      document.documentElement.style.setProperty("--wgt-font-size", `${sizePx}px`);
    }
  };
    
  // Only fetch once a session exists. Login fetches the theme itself, so firing here earlier
  // would send a second request (and one with no token).
  useEffect(() => {
    if (themeStatus === "idle" && adminToken) {
      dispatch(fetchThemeData({ themeId: null }));
    }
  }, [themeStatus, adminToken, dispatch]);

  useEffect(()=>{
    setBackUrl(backButtonUrl);
    setLogoUrl(companyLogoUrl || process.env.REACT_APP_EM_LOGO);
  },[backButtonUrl,companyLogoUrl])

  const fromMobileAppInUrl = searchParams.get('fromMobileApp') === 'true';
  useEffect(() => {
    if (!fromMobileAppInUrl) return;
    try {
      sessionStorage.setItem(FROM_MOBILE_APP_STORAGE_KEY, 'true');
    } catch (_) {
      /* ignore */
    }
  }, [fromMobileAppInUrl]);

  /** True when URL has fromMobileApp=true or we persisted it (e.g. after client-side navigation). */
  const isFromMobileApp = useMemo(() => {
    let fromSession = false;
    try {
      fromSession = sessionStorage.getItem(FROM_MOBILE_APP_STORAGE_KEY) === 'true';
    } catch (_) {
      fromSession = false;
    }
    return fromMobileAppInUrl || fromSession;
  }, [fromMobileAppInUrl, location.pathname]);

  const hideBackButton = useMemo(
    () => shouldHideBackButton(location.pathname, isFromMobileApp),
    [isFromMobileApp, location.pathname]
  );

  const backHistoryPushed = useRef(false);
  useEffect(() => {
    if (!backUrl) return undefined;

    const handleBrowserBack = () => {
      window.location.href = backUrl;
    };

    // Push ONE history entry so browser-back can be captured; pushing on every route change
    // filled the history with duplicates.
    if (!backHistoryPushed.current) {
      window.history.pushState({ lcpBackHandled: true }, "", window.location.href);
      backHistoryPushed.current = true;
    }
    window.addEventListener("popstate", handleBrowserBack);

    return () => {
      window.removeEventListener("popstate", handleBrowserBack);
    };
  }, [backUrl]);


  useEffect(() => {
    if (themeStatus !== "succeeded" || !themeData) return undefined;
    applyCustomTheme(themeData);

    const syncBackground = () => applyThemeBodyBackground(themeData);
    syncBackground();

    const mq = window.matchMedia("(max-width: 768px)");
    const onMqChange = () => syncBackground();
    if (mq.addEventListener) {
      mq.addEventListener("change", onMqChange);
    } else {
      mq.addListener(onMqChange);
    }
    window.addEventListener("resize", syncBackground);
    window.addEventListener("orientationchange", syncBackground);

    return () => {
      if (mq.removeEventListener) {
        mq.removeEventListener("change", onMqChange);
      } else {
        mq.removeListener(onMqChange);
      }
      window.removeEventListener("resize", syncBackground);
      window.removeEventListener("orientationchange", syncBackground);
    };
  }, [themeStatus, themeData]);

  const handleLogoClick = (e) => {
    if (!isFromMobileApp) return;
    e.preventDefault();
    window.GameStatus?.postMessage?.('navigate_back');
  };

  return (
    <div className="ddd-user-shell">
      <header className="upperaction">
      <a href={process.env.REACT_APP_BASE_URL} onClick={handleLogoClick}><img src={logoUrl} className="logo-holder" alt="Company logo" /></a>
        {!hideBackButton && backUrl ? (
        <div className="back-holder">
       <a href={backUrl} className="back-default">Back</a>
        </div>
        ) : null}
       
      </header>
      <div className="ddd-user-main">{children}</div>
    </div>
  );
}

export default USER;
